import React, { useState } from 'react';
import styled from 'styled-components';
import glassTheme, { glassMixins } from '../../styles/glassTheme';
import { TermsAcceptedData } from '../../services/authApi';

// 协议版本号 - 当协议更新时需要修改此值
const CURRENT_AGREEMENT_VERSION = '2026-03-24';
const CURRENT_PRIVACY_VERSION = '2026-03-24';

const Container = styled.div`
  ${glassTheme.glass.heavy}
  padding: 2rem;
  width: 90%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${glassTheme.colors.accent.blue};
    border-radius: 3px;
  }
`;

const Title = styled.h2`
  text-align: center;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
  background: ${glassTheme.gradients.cyber};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const TabContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Tab = styled.button<{ $active: boolean }>`
  flex: 1;
  padding: 0.75rem 1rem;
  background: ${props => props.$active
    ? glassTheme.gradients.cyber
    : 'rgba(0, 0, 0, 0.3)'};
  border: 2px solid ${props => props.$active
    ? 'transparent'
    : glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  color: ${glassTheme.colors.text.primary};
  cursor: pointer;
  transition: all ${glassTheme.transition.base};
  font-weight: 600;
  font-size: 0.9rem;

  &:hover {
    border-color: ${glassTheme.colors.accent.blue};
  }
`;

const ContentBox = styled.div`
  background: rgba(0, 0, 0, 0.4);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  padding: 1.5rem;
  max-height: 350px;
  overflow-y: auto;
  margin-bottom: 1.5rem;
  font-size: 0.875rem;
  line-height: 1.8;
  color: ${glassTheme.colors.text.secondary};

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 0, 0, 0.2);
  }

  &::-webkit-scrollbar-thumb {
    background: ${glassTheme.colors.accent.blue};
    border-radius: 2px;
  }

  h1 {
    font-size: 1.1rem;
    color: ${glassTheme.colors.text.primary};
    margin-bottom: 1rem;
    text-align: center;
  }

  p {
    margin-bottom: 1rem;
    text-indent: 2em;
  }

  strong {
    color: ${glassTheme.colors.text.primary};
  }
`;

const DocTitle = styled.h3`
  text-align: center;
  font-size: 1.1rem;
  color: ${glassTheme.colors.text.primary};
  margin-bottom: 1.5rem;
  font-weight: 600;
`;

const CheckboxContainer = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid ${glassTheme.colors.border};
  border-radius: ${glassTheme.borderRadius.md};
  transition: all ${glassTheme.transition.base};

  &:hover {
    border-color: ${glassTheme.colors.accent.blue};
  }
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  accent-color: ${glassTheme.colors.accent.blue};
  cursor: pointer;
`;

const CheckboxText = styled.span`
  font-size: 0.875rem;
  color: ${glassTheme.colors.text.primary};
  line-height: 1.5;

  a {
    color: ${glassTheme.colors.accent.blue};
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button<{ $primary?: boolean }>`
  ${glassMixins.button}
  flex: 1;
  padding: 0.75rem 1.5rem;
  font-size: 0.9rem;
  ${props => props.$primary && `
    background: ${glassTheme.gradients.cyber};
  `}

  &:hover {
    box-shadow: ${glassTheme.shadow.xl}, ${glassTheme.shadow.glow};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// 用户协议内容（不含标题）
const USER_AGREEMENT_TITLE = '"StatQuest with AI" 用户协议';
const USER_AGREEMENT_CONTENT = `欢迎您使用"StatQuest with AI"（以下简称"本网站"）。在使用本网站提供的各项服务之前，请您务必仔细阅读并透彻理解本《用户协议》（以下简称"本协议"）。您注册、登录、使用本网站服务的行为，即表示您已阅读、理解并同意接受本协议全部条款的约束。

**1. 网站性质与服务内容**

**1.1 服务宗旨：** 本网站是一个专注于帮助用户学习如何应用统计学的教育和辅助工具平台。我们通过结合人工智能技术，为您提供统计学知识的讲解、数据分析案例演示及相关学习资源。

**1.2 AI 辅助性质：** 网站提供的 AI 答疑、代码生成或分析建议仅供学习和参考之用。由于人工智能的技术局限性，我们无法保证所有 AI 生成内容的 100% 准确性。用户在将所学知识应用于实际的重要决策（如商业、医疗、金融等）时，应自行判断并承担相应风险。

**2. 隐私保护与数据使用（核心条款）**

**2.1 绝对保密承诺：** 我们高度尊重并保护您的个人隐私。您在注册和使用本网站过程中产生的所有个人数据（包括但不限于姓名、邮箱、联系方式、上传的私人数据集等）均会受到严格保密，未经您明确授权，我们绝不会向任何第三方出售、出租或非法披露您的个人信息。

**2.2 学术研究授权：** 为了促进统计学教育和人工智能领域的进步，您同意您的使用数据（例如学习进度、向 AI 提出的统计学问题、互动模式等）可能被用于学术研究和论文发表。

**2.3 身份信息隔离保证：** 我们在此郑重承诺：所有用于学术研究的数据均会经过严格的去标识化和匿名化处理。在任何研究成果、论文或公开报告中，绝不会包含任何可能泄露您真实身份的信息。

**3. 用户账号与安全**

**3.1** 您在注册账号时应提供真实、有效的资料。

**3.2** 您需妥善保管账号密码，任何通过您账号进行的操作均视为您本人的行为。若发现账号被盗用或存在安全漏洞，请立即联系我们。

**4. 用户行为规范**
在使用本网站服务时，您必须遵守相关法律法规，不得从事以下行为：

**4.1** 利用本网站进行任何违法、侵权或违背公序良俗的活动；

**4.2** 上传、发布包含病毒、木马或恶意代码的文件；

**4.3** 使用自动化程序（如爬虫、机器人）恶意抓取本网站的内容或过度消耗 AI 接口资源，干扰网站正常运行；

**4.4** 将本网站的 AI 系统用于生成欺诈性报告、学术造假或任何不道德的数据操纵。

**5. 知识产权**

**5.1** 本网站提供的所有官方内容（包括但不限于文字、图像、架构、算法模型等）的知识产权均归"StatQuest with AI"所有。

**5.2** 您利用本网站 AI 工具辅助生成的代码、图表和分析报告，其使用权归您所有，但您需确保该生成不侵犯任何第三方的合法权益。

**6. 免责声明**

**6.1 教育目的：** 本网站提供的所有内容和工具仅供教育目的。我们不对您因使用本网站内容而造成的任何直接、间接或附带损失承担法律责任。

**6.2 服务中断：** 如因不可抗力、网络故障、系统维护等原因导致服务中断，我们将在能力范围内尽快恢复，但对此不承担违约责任。

**7. 协议的修改与终止**

**7.1** 我们保留随时修改本协议的权利。协议条款一旦发生重大变更，我们将通过网站公告或电子邮件等方式通知您。如果您继续使用本网站，即视为接受修改后的协议。

**7.2** 如果您违反了本协议的相关规定，我们有权在不事先通知的情况下，限制、暂停或终止您的账号及服务。

**8. 适用法律与管辖**
本协议的订立、执行和解释及争议的解决均应适用本网站运营主体所在地的法律。如双方就本协议内容或其执行发生任何争议，应尽量友好协商解决；协商不成时，任何一方均可向运营主体所在地有管辖权的人民法院/法院提起诉讼。`;

// 隐私政策内容（不含标题）
const PRIVACY_POLICY_TITLE = '"StatQuest with AI" 隐私政策';
const PRIVACY_POLICY_CONTENT = `"StatQuest with AI"（以下简称"我们"或"本网站"）深知隐私对您的重要性，并致力于保护您的个人数据。本《隐私政策》旨在透明地向您说明我们在您使用网站服务时，如何收集、使用、存储和保护您的信息，特别是关于我们将数据用于学术研究时的脱敏和保密措施。

**1. 我们收集哪些信息**
为了向您提供优质的统计学学习和 AI 辅助服务，我们会收集以下类别的信息：

**1.1 账户信息：** 当您注册时，我们收集的姓名、电子邮件地址和密码。

**1.2 交互与学习数据：** 您向 AI 提出的统计学问题、生成的代码、对话记录、学习进度、测试成绩以及您主动上传用于分析的练习数据集。

**1.3 设备与日志数据：** 当您访问网站时，系统自动接收并记录的信息，包括 IP 地址、浏览器类型、访问时间、页面浏览记录等。

**2. 我们如何使用您的信息**
我们收集的数据将用于以下目的：

**2.1 提供和维护服务：** 响应您的学习需求，提供个性化的统计学知识讲解和 AI 答疑。

**2.2 服务改进：** 分析网站的使用情况，以优化页面设计、提升 AI 模型的准确性和交互体验。

**2.3 学术研究（重点说明请见第 3 节）：** 探索 AI 在统计学教育中的应用模式和效果。

**2.4 沟通：** 向您发送服务通知、账户更新或安全警报。

**3. 学术研究与数据匿名化承诺（核心条款）**
正如《用户协议》所述，为了推动教育学和人工智能领域的发展，您的"交互与学习数据"可能被用于学术分析和论文撰写。为此，我们实施了极其严格的数据保护机制，确保您的身份绝对安全：

**3.1 彻底的去标识化（De-identification）：** 在数据进入研究资料库之前，系统会自动剥离所有可能直接识别您身份的信息（如姓名、邮箱、IP地址等），并分配一个随机生成的虚拟研究 ID。

**3.2 数据聚合处理（Aggregation）：** 学术研究主要关注群体性趋势（例如："在使用特定 AI 提示词后，70% 的用户更快掌握了线性回归"），而非追踪单一用户的具体行为。我们在论文和报告中只会披露聚合后的宏观统计数据。

**3.3 系统物理与逻辑隔离：** 您的个人账户信息（明文）与用于研究的匿名数据集存储在完全独立的数据库中。研究人员（包括我们的内部数据科学家）在进行学术分析时，绝无权限访问或反向追踪您的真实身份。

**4. 数据安全与存储**

**4.1 加密技术：** 我们采用行业标准的 SSL/TLS 加密技术来保障数据在传输过程中的安全。您的个人信息在服务器端同样经过高级加密存储。

**4.2 访问控制：** 仅有经过严格授权且签署了保密协议的内部员工，在履行职责（如系统维护）时才能接触到必要的用户数据。

**5. 我们如何共享信息**
我们承诺：绝不出售或出租您的任何个人信息。我们仅在以下极其有限的情况下共享信息：

**5.1 服务提供商：** 为了维持网站运行（如云服务器托管、邮件发送服务），我们可能与值得信赖的第三方服务商共享必要数据。这些服务商均受到严格的保密协议约束。

**5.2 法律要求：** 在应国家法律法规要求、法庭命令或政府部门的合法强制性要求时，我们可能会披露信息。

**6. 您的权利与选择**
针对您的个人信息，您拥有以下权利：

**6.1 访问与更正：** 您可以在账户设置中随时查看和修改您的个人资料。

**6.2 删除权（被遗忘权）：** 如果您希望彻底注销账户并删除所有关联数据，请联系我们的客服团队。我们会在验证您的身份后，在法定时限内从活动数据库中永久抹除您的个人信息（已不可逆匿名化的历史研究数据除外）。

**6.3 选择退出：** 您可以选择不接收我们的非必要通知邮件。

**7. 儿童隐私**
本网站主要面向成年人及具备完全民事行为能力的用户。如果您是未成年人，请在父母或法定监护人的指导和同意下使用本网站并提供信息。如果我们发现在未事先获得可证实的父母同意的情况下收集了儿童的个人信息，我们将设法尽快删除相关数据。

**8. 联系我们**
如果您对本《隐私政策》或您的数据安全有任何疑问、意见或建议，请通过以下方式与我们联系：

电子邮箱：genghushi@foxmail.com`;

interface TermsAgreementProps {
  onAgree: (data: TermsAcceptedData) => void;
  onCancel: () => void;
}

// 将 **文本** 转换为 <strong>文本</strong> 的函数
const renderBoldText = (text: string) => {
  return text.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
};

export const TermsAgreement: React.FC<TermsAgreementProps> = ({ onAgree, onCancel }) => {
  const [activeTab, setActiveTab] = useState<'agreement' | 'privacy'>('agreement');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToPrivacy, setAgreedToPrivacy] = useState(false);

  const canProceed = agreedToTerms && agreedToPrivacy;

  const handleAgree = () => {
    const now = new Date().toISOString();
    const data: TermsAcceptedData = {
      userAgreement: {
        agreed: true,
        version: CURRENT_AGREEMENT_VERSION,
        agreedAt: now
      },
      privacyPolicy: {
        agreed: true,
        version: CURRENT_PRIVACY_VERSION,
        agreedAt: now
      }
    };
    onAgree(data);
  };

  return (
    <Container>
      <Title>服务条款与隐私政策</Title>

      <TabContainer>
        <Tab
          $active={activeTab === 'agreement'}
          onClick={() => setActiveTab('agreement')}
        >
          用户协议
        </Tab>
        <Tab
          $active={activeTab === 'privacy'}
          onClick={() => setActiveTab('privacy')}
        >
          隐私政策
        </Tab>
      </TabContainer>

      <ContentBox>
        {activeTab === 'agreement' ? (
          <>
            <h3 style={{ textAlign: 'center', color: glassTheme.colors.text.primary, marginBottom: '1rem' }}>{USER_AGREEMENT_TITLE}</h3>
            <div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: renderBoldText(USER_AGREEMENT_CONTENT) }} />
          </>
        ) : (
          <>
            <h3 style={{ textAlign: 'center', color: glassTheme.colors.text.primary, marginBottom: '1rem' }}>{PRIVACY_POLICY_TITLE}</h3>
            <div style={{ whiteSpace: 'pre-wrap' }} dangerouslySetInnerHTML={{ __html: renderBoldText(PRIVACY_POLICY_CONTENT) }} />
          </>
        )}
      </ContentBox>

      <CheckboxContainer>
        <Checkbox
          type="checkbox"
          checked={agreedToTerms}
          onChange={(e) => setAgreedToTerms(e.target.checked)}
        />
        <CheckboxText>
          我已阅读并同意《<strong onClick={() => setActiveTab('agreement')} style={{ cursor: 'pointer', color: glassTheme.colors.accent.blue }}>用户协议</strong>》
        </CheckboxText>
      </CheckboxContainer>

      <CheckboxContainer>
        <Checkbox
          type="checkbox"
          checked={agreedToPrivacy}
          onChange={(e) => setAgreedToPrivacy(e.target.checked)}
        />
        <CheckboxText>
          我已阅读并同意《<strong onClick={() => setActiveTab('privacy')} style={{ cursor: 'pointer', color: glassTheme.colors.accent.blue }}>隐私政策</strong>》，了解我的数据可能被用于学术研究（经过脱敏处理）
        </CheckboxText>
      </CheckboxContainer>

      <ButtonContainer>
        <Button onClick={onCancel}>
          取消
        </Button>
        <Button $primary onClick={handleAgree} disabled={!canProceed}>
          同意并继续
        </Button>
      </ButtonContainer>
    </Container>
  );
};
